/**
 * Sweetbox Add-ons Popup
 * Handles the popup functionality for adding product add-ons to gift boxes.
 */
class SweetboxPopup {
  constructor() {
    // DOM Elements
    this.popup = document.getElementById('sweetboxPopup');
    if (!this.popup) return; // Popup not on this page

    this.popupContainer = this.popup.querySelector('.sweetbox-popup-container');
    this.closeBtn = this.popup.querySelector('[data-close-popup]');
    this.categoriesScreen = document.getElementById('sweetboxCategoriesScreen');
    this.productsScreen = document.getElementById('sweetboxProductsScreen');
    this.categoriesGrid = document.getElementById('sweetboxCategoriesGrid');
    this.productsGrid = document.getElementById('sweetboxProductsGrid');
    this.contextualGrid = document.getElementById('sweetboxContextualGrid');
    this.categoryTitle = this.popup.querySelector('.category-title');
    this.backBtn = document.getElementById('sweetboxBackBtn');
    this.loadingElement = document.getElementById('sweetboxLoading');
    this.successMessage = document.getElementById('sweetboxSuccessMessage');
    this.successText = document.getElementById('sweetboxSuccessText');

    this.categoriesData = [];
    this.currentProduct = null;

    this.lastTriggerBtn = null;
    this.lastCategoryCard = null;

    this.init();
  }

  /**
   * Initialize the popup
   */
  init() {
    if (!this.popup) return;

    // Load categories data
    this.loadCategoriesData();

    // Add event listeners
    this.setupEventListeners();
  }

  /**
   * Load categories data from hidden JSON script
   */
  loadCategoriesData() {
    // Initialize with empty array as fallback
    this.categoriesData = [];

    const dataElement = document.getElementById('sweetboxCategoriesData');
    if (!dataElement) {
      console.error('Sweetbox data element not found. Make sure the popup section is included correctly.');
      return;
    }

    try {
      const data = JSON.parse(dataElement.textContent);

      // Validate data structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid data structure: expected object');
      }

      if (!Array.isArray(data.categories)) {
        throw new Error('Invalid data structure: categories should be an array');
      }

      this.categoriesData = data.categories || [];
      this.currentProduct = data.currentProduct || null;
      if (this.contextualGrid && Array.isArray(data.contextualProducts)) {
        this.contextualGrid.hidden = data.contextualProducts.length === 0;
        if (data.contextualProducts.length) {
          this.renderProducts({ products: data.contextualProducts }, this.contextualGrid);
        }
      }

      console.log('Sweetbox categories loaded:', this.categoriesData.length);
    } catch (error) {
      console.error('Error parsing Sweetbox categories data:', error);
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Open popup (supports multiple triggers anywhere on the page)
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.sweetbox-trigger-btn');
      if (!trigger) return;
      e.preventDefault();
      this.openPopup(trigger);
    });

    // Close popup
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closePopup());
    }

    // Click outside to close - only if clicking directly on overlay background
    this.popup.addEventListener('click', (e) => {
      if (e.target === this.popup) {
        this.closePopup();
      }
    });

    // Keep keyboard handling inside the open dialog, not on the document.
    this.popup.addEventListener('keydown', (e) => {
      if (!this.popup.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.closePopup();
      } else if (e.key === 'Tab') {
        this.containTabFocus(e);
      }
    });

    // Back button
    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => this.showCategories(true));
    }

    // Category cards click (event delegation)
    if (this.categoriesGrid) {
      this.categoriesGrid.addEventListener('click', (e) => {
        const categoryCard = e.target.closest('.sweetbox-category-card');
        if (!categoryCard) return;

        e.preventDefault();
        e.stopPropagation();

        let categoryId = categoryCard.getAttribute('data-category-id') || categoryCard.dataset.categoryId;

        if (categoryId) {
          this.showProducts(categoryId);
        } else {
          console.error('Category ID not found on card:', categoryCard);
        }
      });

      // Keyboard accessibility for category cards
      this.categoriesGrid.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;

        const categoryCard = e.target.closest('.sweetbox-category-card');
        if (!categoryCard) return;

        e.preventDefault();
        const categoryId = categoryCard.getAttribute('data-category-id') || categoryCard.dataset.categoryId;
        if (categoryId) this.showProducts(categoryId);
      });
    }

    // Add to cart buttons (event delegation)
    [this.productsGrid, this.contextualGrid].filter(Boolean).forEach((grid) => {
      grid.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-add-to-cart]');
        if (!addBtn || addBtn.disabled) return;

        e.preventDefault();
        const variantId = addBtn.getAttribute('data-variant-id');
        const productTitle = addBtn.getAttribute('data-product-title');
        if (!variantId) return;

        this.addProductToCart(variantId, productTitle);
      });
    });
  }

  getFocusableElements() {
    if (!this.popupContainer) return [];
    return Array.from(this.popupContainer.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'))
      .filter(element => element.tabIndex >= 0 && !element.disabled &&
        !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
        element.getClientRects().length > 0 &&
        !['hidden', 'collapse'].includes(window.getComputedStyle(element).visibility));
  }

  containTabFocus(event) {
    const focusable = this.getFocusableElements();
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (!first) {
      event.preventDefault();
      if (this.popupContainer) {
        this.popupContainer.setAttribute('tabindex', '-1');
        this.popupContainer.focus();
      }
    } else if (active === this.popupContainer || active === this.popup ||
      (event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  }

  openPopup(triggerEl) {
    this.lastTriggerBtn = triggerEl || this.lastTriggerBtn;
    this.lastCategoryCard = null;

    this.popup.classList.add('is-open');
    this.popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Show categories by default
    this.showCategories();

    // Focus immediately so a delayed callback cannot steal focus after closing.
    if (this.closeBtn) this.closeBtn.focus();
  }

  closePopup() {
    this.popup.classList.remove('is-open');
    this.popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Reset to categories view
    this.showCategories();

    // Return focus
    if (this.lastTriggerBtn) this.lastTriggerBtn.focus();
  }

  /**
   * Show categories screen
   */
  showCategories(restoreFocus = false) {
    if (this.categoriesScreen) this.categoriesScreen.style.display = 'block';
    if (this.productsScreen) this.productsScreen.style.display = 'none';
    if (restoreFocus && this.popup.classList.contains('is-open')) {
      const target = this.lastCategoryCard && this.categoriesGrid?.contains(this.lastCategoryCard)
        ? this.lastCategoryCard : this.closeBtn;
      if (target) target.focus();
    }
  }

  /**
   * Show products for a selected category
   */
  showProducts(categoryId) {
    if (!this.categoriesData || !this.categoriesData.length) return;

    const category = this.categoriesData.find(cat => String(cat.id) === String(categoryId));
    if (!category) {
      console.error('Category not found for ID:', categoryId);
      return;
    }

    this.lastCategoryCard = Array.from(this.categoriesGrid?.querySelectorAll('.sweetbox-category-card') || [])
      .find(card => String(card.getAttribute('data-category-id')) === String(categoryId)) || null;

    if (this.categoriesScreen) this.categoriesScreen.style.display = 'none';
    if (this.productsScreen) this.productsScreen.style.display = 'block';

    // Update category title
    if (this.categoryTitle && category.title) {
      this.categoryTitle.textContent = category.title;
    }

    this.renderProducts(category);

    if (this.popup.classList.contains('is-open')) {
      const target = this.categoryTitle || this.backBtn || this.closeBtn;
      if (target) {
        if (target === this.categoryTitle) target.setAttribute('tabindex', '-1');
        target.focus();
      }
    }
  }

  /**
   * Render products grid for selected category
   */
  renderProducts(category, grid = this.productsGrid) {
    if (!grid) return;

    const products = Array.isArray(category.products) ? category.products : [];
    if (!products.length) {
      grid.classList.add('empty');
      grid.innerHTML = '<div class="sweetbox-empty-message">אין מוצרים זמינים בקטגוריה זו</div>';
      return;
    }

    // clean empty state
    grid.classList.remove('empty');

    grid.innerHTML = products.map(p => {
      const price = this.formatPrice(p.price);
      const compareAt = p.compareAtPrice ? this.formatPrice(p.compareAtPrice) : null;

      return `
        <div class="sweetbox-product-card">
        <a href="${p.url}" class="sweetbox-product-link" target="_blank">
          <div class="sweetbox-product-img-wrap">
            ${p.image ? `<img src="${p.image}" alt="${this.escapeHtml(p.title)}" loading="lazy" />` : ''}
          </div>
          <h4 class="sweetbox-product-title">${this.escapeHtml(p.title)}</h4>
        </a>

          <div class="sweetbox-product-info">
            <div class="sweetbox-product-prices">
              <span class="sweetbox-product-price">${price}</span>
              ${compareAt ? `<span class="sweetbox-product-compare">${compareAt}</span>` : ''}
            </div>

            <button
              type="button"
              class="sweetbox-add-to-cart-btn"
              data-add-to-cart
              data-variant-id="${this.escapeHtml(String(p.variantId))}"
              data-product-title="${this.escapeHtml(p.title)}"
              aria-label="Add ${this.escapeHtml(p.title)} to cart"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    }).join('');
  }

  async addProductToCart(variantId, productTitle) {
    if (this.isAdding) return;
    this.isAdding = true;
    const addButtons = Array.from(this.popup.querySelectorAll('[data-add-to-cart]'));
    const disabledStates = addButtons.map(button => button.disabled);
    addButtons.forEach(button => { button.disabled = true; });
    this.popup.setAttribute('aria-busy', 'true');
    try {
      this.showLoading(true);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                box: this.currentProduct?.title || '',
                _sweetbox_box_product: this.currentProduct?.title || '',
                _sweetbox_box_sku: this.currentProduct?.sku || ''
              }
            }
          ]
        })

      });

      if (!res.ok) throw new Error('Failed to add to cart');

      this.showSuccessMessage(productTitle);
      await this.refreshCartHeader();
    } catch (err) {
      console.error(err);
      alert('משהו השתבש בהוספה לעגלה');
    } finally {
      this.showLoading(false);
      addButtons.forEach((button, index) => { button.disabled = disabledStates[index]; });
      this.popup.removeAttribute('aria-busy');
      this.isAdding = false;
    }
  }

  async refreshCartHeader() {
    try {
      // Same read-only section refresh used by the theme cart; never estimate counts.
      const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
      const response = await fetch(cartUrl + '?sections=header', { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart header refresh failed');
      const sections = await response.json();
      if (typeof sections.header !== 'string' || !sections.header) throw new Error('Cart header section missing');
      const next = new DOMParser().parseFromString(sections.header, 'text/html');
      ['.page-header .header-cart', '.docked-navigation-container .header-cart', '.mobile-cart'].forEach(selector => {
        const replacement = next.querySelector(selector) ||
          (selector === '.docked-navigation-container .header-cart' ? next.querySelector('.header-cart') : null);
        if (!replacement) return;
        document.querySelectorAll(selector).forEach(current => {
          current.innerHTML = replacement.innerHTML;
          if (replacement.hasAttribute('aria-label')) current.setAttribute('aria-label', replacement.getAttribute('aria-label'));
        });
      });
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('theme:cart:change'));
    } catch (error) {
      // The item was already added: a display refresh failure must not invite a duplicate add.
      console.warn('Sweetbox cart header refresh failed', error);
    }
  }

  showSuccessMessage(productTitle) {
    if (!this.successMessage || !this.successText) return;

    this.successText.textContent = `נוסף לעגלה: ${productTitle || ''}`;
    this.successMessage.style.display = 'flex';

    setTimeout(() => {
      this.successMessage.style.display = 'none';
    }, 2000);
  }

  showLoading(isLoading) {
    if (!this.loadingElement) return;
    this.loadingElement.style.display = isLoading ? 'flex' : 'none';
  }

  formatPrice(cents) {
    if (typeof cents !== 'number') return '';
    const value = (cents / 100).toFixed(2);
    return `₪${value}`;
  }

  escapeHtml(str) {
    return String(str || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new SweetboxPopup();
});